<script setup lang="ts">
  import { z } from 'zod';
  import { storeToRefs } from 'pinia';
  import {
    MpBox,
    MpButton,
    MpDatePicker,
    MpField,
    MpInput,
    MpSelect,
    MpText,
    MpUploadSurface,
    pushToastMessage,
  } from '@borg/ui';
  import { EditModalEvents } from '@/layers/MyProfile/components/EditModal/types';

  defineProps<{
    isOpen: boolean;
  }>();

  const emits = defineEmits<EditModalEvents>();

  const { t } = useI18n();
  const userStore = useUserStore();

  const { data: educationLevels } = await educationLevelsRepository.getMany();

  const { upload, isUploading } = useFileUpload({ source: 'userAvatar' });
  const inProgress = computed(() => userStore.patchState === 'inProgress' || isUploading.value);

  const { user } = storeToRefs(userStore);
  const ACCEPTED_FILE_TYPES = ['image/png', 'image/jpeg'];
  // 2MB
  const MAX_FILE_SIZE = 2000000;

  // todo - somehow 'File' became an unknown type, changed to 'any'
  const { values, state, validate, resetStates, setValues } = useForm({
    initialData: () => transformEntity(),
    schema: z.object({
      profileImageFile: z
        .any()
        .refine((file) => file.size <= MAX_FILE_SIZE, t('FILE_VALIDATOR.FILE_TOO_BIG'))
        .refine(
          (file) => ACCEPTED_FILE_TYPES.includes(file.type),
          t('FILE_VALIDATOR.WRONG_FILE_TYPE', {
            formats: getExtensionsFromMimeTypeArray(ACCEPTED_FILE_TYPES),
          }),
        )
        .optional(),
      image: z.object({ id: z.number(), url: z.string(), name: z.string().nullable() }).nullable(),
      firstName: z.string().min(1, t('REQUIRED_INPUT')),
      lastName: z.string().min(1, t('REQUIRED_INPUT')),
      phone: z.object({ countryCode: z.string(), number: z.string() }).nullable().optional(),
      contactEmail: z.union([
        z.string().email(t('INVALID_EMAIL_FORMAT')),
        z.literal(''),
        z.undefined(),
      ]),
      educationLevel: z.object({ id: z.number(), name: z.string() }).optional(),
      address: z.string().optional(),
      title: z.string().optional(),
      keywords: z.string().optional(),
      dateOfBirth: z.union([z.date(), z.null()]),
      citizenship: z.string().optional(),
      linkedin: z
        .string()
        .min(5, t('MIN_LENGTH', { n: 5 }))
        .optional()
        .or(z.literal('')),
      webSite: z
        .string()
        .min(5, t('MIN_LENGTH', { n: 5 }))
        .optional()
        .or(z.literal('')),
    }),
  });

  async function onAvatarChange(fileList: FileList) {
    const file = fileList.length > 0 ? fileList[0] : undefined;
    setValues({ profileImageFile: file });
    await validate(false);

    if (!state.profileImageFile.hasError) {
      const { onSuccess, onError } = upload(fileList);

      onSuccess((file) => {
        const { id, url, name } = file;
        setValues({ image: { id: parseInt(id), url, name } });
      });

      onError(() => {
        pushToastMessage({
          type: 'alert',
          title: '',
          subtitle: '',
        });
      });
    }
  }

  function transformEntity(input?: typeof user.value) {
    return {
      profileImageFile: undefined as undefined | File,
      image: input ? input.image : null,
      firstName: input?.firstName ?? '',
      lastName: input?.lastName ?? '',
      phone: input?.phone ?? undefined,
      contactEmail: input?.contactEmail ?? undefined,
      educationLevel: input?.educationLevel ?? undefined,
      address: input?.address ?? undefined,
      title: input?.title ?? undefined,
      keywords: input?.keywords ?? undefined,
      dateOfBirth: toDate(input?.dateOfBirth),
      citizenship: input?.citizenship ?? undefined,
      linkedin: input?.linkedin ?? undefined,
      webSite: input?.webSite ?? undefined,
    };
  }

  function onModalOpened() {
    setValues(transformEntity(userStore.user));
  }

  function getFullStringOrNull(value?: string) {
    return value && value.length ? value : null;
  }

  async function onSubmit() {
    // attempt to set an invalid profile picture shouldn't prevent saving other data
    setValues({ profileImageFile: undefined });
    const valid = await validate();

    if (valid) {
      const _values = {
        ...values,
        contactEmail: getFullStringOrNull(values.contactEmail),
        phone: values.phone?.number ? values.phone : null,
        educationLevel: values.educationLevel ?? null,
        title: getFullStringOrNull(values.title),
        keywords: getFullStringOrNull(values.keywords),
        dateOfBirth: getLocaleDate(values.dateOfBirth),
        citizenship: getFullStringOrNull(values.citizenship),
        linkedin: getFullStringOrNull(values.linkedin),
        webSite: getFullStringOrNull(values.webSite),
      };
      delete _values.profileImageFile;
      await userStore.patch(_values);
      if (userStore.patchState === 'isSuccess') {
        emits('change');
        onClose();
      } else {
        pushToastMessage({
          type: 'alert',
          subtitle: t('MY_PROFILE_PAGE.SUBMIT_ERROR_TOAST.SUBTITLE'),
        });
      }
    }
  }

  function onClose() {
    resetStates();
    emits('update:isOpen', false);
  }
</script>

<template>
  <SiteActionsModal
    :is-open="isOpen"
    :title="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.TITLE')"
    :in-progress="inProgress"
    @open="onModalOpened"
    @confirm="onSubmit"
    @cancel="onClose"
  >
    <MyProfileEditModalForm>
      <MpField
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.IMAGE_LABEL')"
        :error="state.profileImageFile.message"
        class="form__row-full"
      >
        <MpBox
          surface="10"
          :class="{ 'user-avatar': true, 'user-avatar--error': state.profileImageFile.hasError }"
        >
          <Avatar
            :src="values.image?.url"
            :empty="!values.image?.url"
            size="md"
          />
          <span>
            <MpText
              no-margin
              type="body3"
            >
              {{ $t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.AVATAR_DISCLAIMER') }}
            </MpText>
          </span>
          <MpUploadSurface
            :accept="ACCEPTED_FILE_TYPES.join(',')"
            @change="onAvatarChange"
          >
            <template #default="{ open }">
              <MpButton
                v-show="values.image?.url"
                class="user-avatar__action"
                variant="secondary"
                size="sm"
                :text="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.REMOVE_AVATAR_BUTTON_LABEL')"
                :disabled="inProgress"
                @click="values.image = null"
              />
              <MpButton
                v-show="!values.image?.url"
                class="user-avatar__action"
                variant="secondary"
                size="sm"
                :text="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.UPLOAD_AVATAR_BUTTON_LABEL')"
                :is-loading="isUploading"
                :disabled="inProgress"
                @click="open"
              />
            </template>
          </MpUploadSurface>
        </MpBox>
      </MpField>
      <MpInput
        v-model="values.firstName"
        test="personal-information-first-name"
        class="form__row-half"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.FIRST_NAME_LABEL')"
        name="fname"
        :error="state.firstName.message"
        required
      />
      <MpInput
        v-model="values.lastName"
        test="personal-information-last-name"
        class="form__row-half"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.LAST_NAME_LABEL')"
        name="lname"
        :error="state.lastName.message"
        required
      />
      <SelectPhone
        v-model="values.phone"
        class="form__row-half"
        :number-label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.PHONE_NUMBER_LABEL')"
        :error="state.phone.message"
      />
      <MpInput
        v-model="values.contactEmail"
        class="form__row-half"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.EMAIL_LABEL')"
        name="email"
        :error="state.contactEmail.message"
      />
      <MpSelect
        v-model="values.educationLevel"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.EDUCATION_LABEL')"
        :placeholder="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.EDUCATION_PLACEHOLDER')"
        :error="state.educationLevel.message"
        :options="educationLevels ?? []"
        :get-value="(i) => i.id"
        :get-label="(i) => i.name"
      />
      <MpInput
        v-model="values.title"
        :placeholder="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.TITLE_PLACEHOLDER')"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.TITLE_LABEL')"
        :error="state.title.message"
      />
      <MpInput
        v-model="values.keywords"
        :placeholder="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.KEYWORDS_PLACEHOLDER')"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.KEYWORDS_LABEL')"
        :error="state.keywords.message"
      />
      <MpInput
        v-model="values.address"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.ADDRESS_LABEL')"
        :error="state.address.message"
      />
      <span class="form__row-half">
        <MpDatePicker
          v-model="values.dateOfBirth"
          :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.DOB_LABEL')"
          :error="state.dateOfBirth.message"
          initial-pick="year"
          clearable
        />
      </span>
      <MpInput
        v-model="values.citizenship"
        class="form__row-half"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.CITIZENSHIP_LABEL')"
        :error="state.citizenship.message"
      />
      <MpInput
        v-model="values.linkedin"
        class="form__row-half"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.LINKEDIN_LABEL')"
        :error="state.linkedin.message"
      />
      <MpInput
        v-model="values.webSite"
        class="form__row-half"
        :label="$t('MY_PROFILE_PAGE.PERSONAL_INFO_MODAL.WEBSITE_LABEL')"
        :error="state.webSite.message"
      />
    </MyProfileEditModalForm>
  </SiteActionsModal>
</template>

<style scoped lang="scss">
  .user-avatar {
    display: flex;
    align-items: center;
    gap: var(--mp-space-40);
    padding: var(--mp-space-50);
    border-radius: var(--mp-space-20);
    border: 1px dashed var(--mp-color-grey-300);

    &--error {
      border-color: var(--mp-color-status-error);
    }

    &__action {
      min-width: max-content;
      margin-left: auto;
    }

    @include dark-mode {
      border-color: var(--mp-color-grey-500);
    }
  }
</style>
